import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { createFirstAndLastName } from 'utils'
import { Avatar, Menu as MaterialMenu, MenuItem, Modal } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { translate } from 'locales'
import { alertSuccess } from '../../utils/toast'
import TermsModal from 'components/Modal/TermsModal'

import api from 'configs/api'

// Contexts
import { useSignIn } from '../../context/authentication/signin.context'
import { useUser } from '../../context/user/user.context'

import './styles.scss'

// Components
import { Menu, MenuMobile } from '../index'

// Images
import logo from '../../assets/images/logo.svg'
import logoCollapsed from '../../assets/images/logo-star.svg'

const SIDEBAR_COLLAPSED_KEY = '@StarBem:sidebar-collapsed'

const Header: React.FC<{
  openNewTab?: boolean
  onCloseRoom?: () => void
  setAcepptTerm?: () => void
}> = ({ openNewTab = false, onCloseRoom }) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const { logout } = useSignIn()
  const { userData } = useUser()

  const [showTermsModal, setShowTermsModal] = useState(false)
  const [term, setTerm] = useState(false)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [showModal, setShowModal] = React.useState(false)
  const [clickedRoute, setClickedRoute] = React.useState('')
  const [isCollapsed, setIsCollapsed] = useState(
    () => window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  )

  const dataInStorage: any = window.localStorage.getItem('@StarBem:user')
  const dataParse = JSON.parse(dataInStorage)

  const handleTermsStatus = async () => {
    try {
      const response = await api.get('/doctors/ms/v1/me')

      const { terms_authorization } = response.data
      setTerm(terms_authorization)
      window.localStorage.setItem(
        '@StarBem:terms',
        JSON.stringify(terms_authorization)
      )
      if (terms_authorization === true) {
        setShowTermsModal(false)
      } else if (pathname === '/dashboard') {
        setShowTermsModal(true)
      }
    } catch (error) {
      console.error('Erro ao verificar o status dos termos:', error)
    }
  }

  const handleAcceptTerms = () => {
    setShowTermsModal(false)
    handleTermsStatus()
    window.location.reload()
  }

  const handleDenyingTerms = () => {
    setShowTermsModal(false)
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const toggleSidebarCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
      return next
    })
  }

  const handleLogout = () => {
    logout()
    history.replace('/')
    alertSuccess(translate('header.disconnectSuccess'))
  }

  const handleRedirect = (route: string) => {
    history.push(route)
  }

  const handleClickMenu = (route: string) => {
    setClickedRoute(route)

    if (openNewTab) {
      setShowModal(true)
    } else {
      handleRedirect(route)
    }
  }

  const leaveVideoCall = (route: string) => {
    const meetEndButton = document.querySelector(
      '[data-testid="video-call-meet-control-end"]'
    ) as HTMLButtonElement | null

    if (meetEndButton) {
      meetEndButton.click()
    } else {
      onCloseRoom?.()
    }

    setShowModal(false)
    handleRedirect(route)
  }

  useEffect(() => {
    handleTermsStatus()
  }, [])

  useEffect(() => {
    document.body.classList.add('with-sidebar')
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed')
    } else {
      document.body.classList.remove('sidebar-collapsed')
    }
    return () => {
      document.body.classList.remove('with-sidebar')
      document.body.classList.remove('sidebar-collapsed')
    }
  }, [isCollapsed])

  useEffect(() => {
    if (pathname === '/faq') {
      setShowTermsModal(false)
    }
  }, [pathname])

  return (
    <header
      id="header"
      className={isCollapsed ? 'header-collapsed' : undefined}
    >
      <button
        type="button"
        className="header-collapse-btn transparent-button"
        onClick={toggleSidebarCollapse}
        aria-label={
          isCollapsed
            ? translate('header.expandSidebar')
            : translate('header.collapseSidebar')
        }
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? (
          <ChevronRight fontSize="small" />
        ) : (
          <ChevronLeft fontSize="small" />
        )}
      </button>
      <div className="header-top">
        <button
          type="button"
          className="header-logo transparent-button"
          onClick={() => handleClickMenu('/dashboard')}
        >
          <img src={isCollapsed ? logoCollapsed : logo} alt="Starbem" />
        </button>
        <MenuMobile
          openNewTab={openNewTab}
          actionNewTab={handleClickMenu}
          setShowTermsModal={() => setShowTermsModal(true)}
        />
      </div>
      <Menu
        openNewTab={openNewTab}
        setShowModal={setShowModal}
        setClickedRoute={setClickedRoute}
        redirect={handleRedirect}
      />
      <div className="header-user">
        <Avatar
          src={userData.profile.photo}
          onClick={isCollapsed ? handleClick : undefined}
          className={isCollapsed ? 'header-user-avatar-clickable' : undefined}
        />
        <button
          type="button"
          className="transparent-button"
          aria-haspopup="true"
          onClick={handleClick}
        >
          <span>{createFirstAndLastName(userData.name)}</span>
        </button>
        <MaterialMenu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => history.push('/perfil')}>
            {translate('header.myProfile')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setShowTermsModal(true)
              handleClose()
            }}
          >
            {' '}
            {translate('header.terms')}
          </MenuItem>
          <MenuItem onClick={() => handleLogout()}>
            {translate('header.disconnect')}
          </MenuItem>
        </MaterialMenu>
      </div>
      {leaveVideoCall && (
        //@ts-ignore
        <Modal
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          open={showModal}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
        >
          <div className="modal-alert-container">
            <div className="modal-alert-description">
              Você está com uma vídeo chamada em andamento, se sair agora você
              será desconectado e o paciente não conseguirá te ver nem ouvir!
            </div>
            <div className="modal-alert-description modal-alert-confirmation">
              Deseja realmente sair?
            </div>
            <div className="modal-alert-buttons">
              <button
                className="modal-alert-btn b-yes"
                onClick={() => leaveVideoCall(clickedRoute)}
              >
                Sim
              </button>
              <button
                className="modal-alert-btn b-no"
                onClick={() => setShowModal(false)}
              >
                Não
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showTermsModal && (
        <TermsModal
          userId={dataParse.id}
          onAccept={handleAcceptTerms}
          term={term}
          inDenying={handleDenyingTerms}
        />
      )}
    </header>
  )
}

export default Header
